import { useEffect, useReducer, useState, type ReactElement } from 'react';
import { Icon } from '@/components/atoms/icon';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { SwipeableCard } from '@/components/swipeable-card';
import { RecordTakeFlow } from '@/components/organisms/record-take-flow';
import { PracticeTimer } from '@/components/organisms/practice-timer';
import { PracticeQueueRail } from '@/components/organisms/practice-queue-rail';
import { PracticeControlsRail } from '@/components/organisms/practice-controls-rail';
import { PracticeControlsStrip } from '@/components/organisms/practice-controls-strip';
import { DroneControl } from '@/components/molecules/drone-control';
import { DroneSheet } from '@/components/molecules/drone-sheet';
import { ControlTile } from '@/components/molecules/control-tile';
import { PracticeBottomSheet } from '@/components/organisms/practice-bottom-sheet';
import { DEFAULT_BPM, TEMPO_MULTIPLIERS } from '@/components/organisms/practice-controls-constants';
import { parseTuneKey } from '@/lib/practice/parse-tune-key';
import type { DroneKey } from '@/lib/practice/drone-keys';
import { masteryFromSortOrder } from '@/lib/mastery';
import { cn } from '@/lib/cn';
import { formatMinutesSeconds, elapsedSecondsSince } from '@/lib/format-time';
import { type Viewport } from '@/lib/hooks/use-viewport';
import { queueRowSubtitle } from '@/lib/practice/queue-row-subtitle';
import { useMetronome } from '@/lib/hooks/use-metronome';
import { useDroneState } from '@/lib/hooks/use-drone-state';
import { resumeAudioContext } from '@/lib/audio/audio-context';
import { ResourceList } from '@/components/molecules/resource-list';
import { useArrangementResources } from '@/lib/queries/resources';
import type { QueueItem, AssessmentLevel, ArrangementResource } from '@/lib/types';

const MAX_STARS = 3;
const PLANNED_SESSION_SECONDS = 45 * 60;

function StarRow({ sortOrder }: { sortOrder: number }): ReactElement {
  const descriptor = masteryFromSortOrder(sortOrder);
  const fullStars = Math.floor(descriptor.stars);
  const hasHalfStar = descriptor.stars - fullStars === 0.5;
  const emptyStars = MAX_STARS - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <span className="inline-flex items-center text-[var(--brand)]" aria-label={descriptor.shortLabel}>
      {Array.from({ length: fullStars }).map((_, i) => (
        <Icon key={`f-${i}`} name="star" size={16} />
      ))}
      {hasHalfStar && <Icon key="h" name="star_half" size={16} />}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <Icon key={`e-${i}`} name="star" size={16} className="opacity-30" />
      ))}
    </span>
  );
}

// Tablet portrait is a stage-local variant: the shared Viewport type stays a
// 3-bucket width split (every other consumer buckets on mobile/tablet/desktop).
// The practice route widens 'tablet' to 'tablet-portrait' via useTabletOrientation.
export type PracticeStageVariant = Viewport | 'tablet-portrait';

export type PracticeStageProps = {
  variant: PracticeStageVariant;
  startTime: Date;
  isRunning: boolean;
  queue: QueueItem[];
  currentIndex: number;
  assessmentLevels: AssessmentLevel[];
  onToggleTimer: () => void;
  onFinish: () => void;
  onAssess: (assessmentLevelId: string) => void;
  onSkip: () => void;
  onEndPractice: () => void;
  isAssessing: boolean;
};

type MobileStageProps = Omit<PracticeStageProps, 'variant'> & {
  resources: ArrangementResource[];
};

export function PracticeStage(props: PracticeStageProps): ReactElement {
  const { variant, ...rest } = props;
  const currentItem = props.queue[props.currentIndex];

  // Tablet (both orientations) uses a slim controls surface with no resources
  // (deferred to #405/#406). Desktop and mobile both surface the current
  // arrangement's resources, so the hook's enabled-guard skips the request on tablet.
  const isTablet = variant === 'tablet' || variant === 'tablet-portrait';
  const resourcesArrangementId = isTablet ? '' : (currentItem?.arrangementId ?? '');
  const { data: arrangementResources } = useArrangementResources(resourcesArrangementId);
  const resources: ArrangementResource[] = arrangementResources ?? [];

  if (variant === 'mobile') {
    return <MobileStage {...rest} resources={resources} />;
  }
  if (variant === 'tablet-portrait') {
    return <TabletPortraitStage {...rest} />;
  }
  return <StudioStage {...props} resources={resources} />;
}

// Mobile follows the design literally — dark hero stands. See 2026-05-03-practice-screen-v2-adoption_clarifications.md Q1.
function MobileStage({
  startTime,
  isRunning,
  queue,
  currentIndex,
  assessmentLevels,
  onToggleTimer,
  onFinish,
  onAssess,
  onSkip,
  onEndPractice,
  isAssessing,
  resources,
}: MobileStageProps): ReactElement {
  const currentItem = queue[currentIndex];
  const total = queue.length;
  const sortedLevels = [...assessmentLevels].sort((a, b) => a.sortOrder - b.sortOrder);
  const upNextItems = queue.slice(currentIndex + 1);
  const activeSubtitle = currentItem ? queueRowSubtitle(currentItem) : null;
  const drone = useDroneState();
  const [droneSheetOpen, setDroneSheetOpen] = useState<boolean>(false);
  const tuneKey = parseTuneKey(currentItem?.keyName ?? null);

  function handleDroneAutoMatch(): void {
    if (tuneKey === null) return;
    drone.setRoot(tuneKey);
    drone.setOn(true);
  }

  return (
    <div className="bg-[var(--bg-stage)] min-h-screen mx-auto flex min-h-[calc(100vh-4rem)] max-w-3xl flex-col items-center justify-start gap-10 px-6 py-10 text-center lg:justify-center">
      <Badge tone="brand" className="tracking-[0.3em]">Session Active</Badge>

      <PracticeTimer
        startTime={startTime}
        isRunning={isRunning}
        onToggle={onToggleTimer}
        onFinish={onFinish}
      />

      {currentItem && (
        <SwipeableCard onSwipeLeft={() => {}} onSwipeRight={onSkip}>
          <div className="space-y-3">
            <h2 className="font-display text-4xl font-semibold tracking-tight text-[var(--brand)] sm:text-5xl lg:text-6xl">
              {currentItem.pieceName}
            </h2>
            <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-[var(--ink-3)]">
              {activeSubtitle ? (
                <span className="font-medium">{activeSubtitle}</span>
              ) : (
                currentItem.keyName && (
                  <span className="flex items-center gap-2 font-medium">
                    <Icon name="music" size={16} aria-hidden />
                    {currentItem.keyName}
                    {currentItem.formName ? ` • ${currentItem.formName}` : ''}
                  </span>
                )
              )}
              {currentItem.keyName && (
                <span className="h-1 w-1 rounded-full bg-white/20" aria-hidden />
              )}
              <StarRow sortOrder={currentItem.masteryLevelSortOrder} />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--ink-3)]">
              Piece {currentIndex + 1} of {total}
            </p>
          </div>
        </SwipeableCard>
      )}

      {upNextItems.length > 0 && (
        <div className="w-full max-w-xl">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-white/50 text-left">
            Up next
          </p>
          <ul role="list" className="space-y-1">
            {upNextItems.map((item) => (
              <li
                key={item.userArrangementId}
                className="flex items-center justify-between px-3 py-2 rounded-[var(--r-md)] bg-white/5"
              >
                <span className="text-sm font-medium text-white truncate">{item.pieceName}</span>
                {item.plannedMinutes !== null && (
                  <span className="shrink-0 ml-2 text-[10px] font-mono bg-white/10 rounded px-1.5 py-0.5 text-white/60">
                    {item.plannedMinutes} min
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="w-full max-w-xl space-y-5">
        {currentItem && (
          <div className="flex justify-center">
            <RecordTakeFlow arrangementId={currentItem.arrangementId} />
          </div>
        )}
        <section aria-label="Resources" className="text-left">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-white/50">
            Resources
          </p>
          <ResourceList resources={resources} />
        </section>
        <div>
          <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-white/50">
            How did it go?
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {sortedLevels.map((level) => (
              <button
                key={level.assessmentLevelId}
                type="button"
                onClick={() => onAssess(level.assessmentLevelId)}
                disabled={isAssessing}
                className={cn(
                  'flex min-h-[56px] items-center justify-center rounded-[var(--r-md)] border border-white/10',
                  'bg-white/5 px-3 py-3 text-sm font-bold text-white',
                  'transition-all hover:border-[var(--brand)]',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
                aria-label={level.prettyName}
              >
                {level.prettyName}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          aria-label="Drone"
          onClick={() => setDroneSheetOpen(true)}
          className={cn(
            'flex w-full items-center justify-center gap-2 rounded-[var(--r-md)] border border-white/10',
            'bg-white/5 px-3 py-3 text-sm font-bold text-white',
            'transition-all hover:border-[var(--brand)]',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]',
          )}
        >
          <Icon name="music" size={16} aria-hidden />
          Drone
          {drone.on && (
            <span className="ml-1 inline-flex h-2 w-2 rounded-full bg-[var(--warm)]" aria-hidden />
          )}
        </button>

        <div className="flex gap-3">
          <Button variant="secondary" size="md" fullWidth onClick={onSkip} disabled={isAssessing}>
            Skip
          </Button>
          <Button variant="danger" size="md" fullWidth onClick={onEndPractice} disabled={isAssessing}>
            End Practice
          </Button>
        </div>
      </div>

      <DroneSheet
        open={droneSheetOpen}
        onClose={() => setDroneSheetOpen(false)}
        root={drone.root}
        interval={drone.interval}
        octave={drone.octave}
        volume={drone.volume}
        on={drone.on}
        onRootChange={drone.setRoot}
        onIntervalChange={drone.setInterval}
        onOctaveChange={drone.setOctave}
        onVolumeChange={drone.setVolume}
        onOnChange={drone.setOn}
        onAutoMatch={handleDroneAutoMatch}
        autoMatchDisabled={tuneKey === null}
      />
    </div>
  );
}

const INITIAL_MULTIPLIER_INDEX = 2;

function multiplierValue(index: number): number {
  const label = TEMPO_MULTIPLIERS[index] ?? '×1';
  return parseFloat(label.replace('×', ''));
}

function StudioStage({
  variant,
  startTime,
  isRunning,
  queue,
  currentIndex,
  assessmentLevels,
  onToggleTimer,
  onFinish,
  onAssess,
  onSkip,
  onEndPractice,
  isAssessing,
  resources,
}: PracticeStageProps & { resources: ArrangementResource[] }): ReactElement {
  const [, tick] = useReducer((n: number) => n + 1, 0);
  const currentItem = queue[currentIndex];
  const [bpm, setBpm] = useState<number>(currentItem?.practiceTempoBeats ?? DEFAULT_BPM);
  const [activeMultiplier, setActiveMultiplier] = useState<number>(INITIAL_MULTIPLIER_INDEX);
  const [metronomeOn, setMetronomeOn] = useState<boolean>(false);
  const drone = useDroneState();
  const tuneKey = parseTuneKey(currentItem?.keyName ?? null);

  function handleDroneAutoMatch(): void {
    if (tuneKey === null) return;
    drone.setRoot(tuneKey);
    drone.setOn(true);
  }

  useEffect(() => {
    setBpm(currentItem?.practiceTempoBeats ?? DEFAULT_BPM);
  }, [currentItem?.userArrangementId, currentItem?.practiceTempoBeats]);

  function handleMetronomeOnChange(next: boolean): void {
    if (next) void resumeAudioContext();
    setMetronomeOn(next);
  }

  function handleToggleTimer(): void {
    void resumeAudioContext();
    onToggleTimer();
  }

  useMetronome({ bpm, multiplier: multiplierValue(activeMultiplier), on: metronomeOn });

  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [isRunning]);

  const total = queue.length;
  const elapsed = elapsedSecondsSince(startTime);
  const sortedLevels = [...assessmentLevels].sort((a, b) => a.sortOrder - b.sortOrder);
  const railWidth: 220 | 300 = variant === 'tablet' ? 220 : 300;

  return (
    <div
      className="flex h-[calc(100vh-4rem)] bg-[var(--bg-surface)] text-[var(--ink)]"
      style={{ containerType: 'inline-size' }}
    >
      <style>{`
        @container (max-width: 1100px) {
          [data-practice-queue-rail] { width: 240px !important; }
          [data-practice-controls-rail]  { width: 220px !important; }
          .dp-title { font-size: 40px !important; }
        }
        @container (max-width: 900px) {
          [data-practice-queue-rail] { width: 220px !important; }
          [data-practice-controls-rail]  { width: 200px !important; }
          .dp-title { font-size: 32px !important; }
          .dp-stats { max-width: 100% !important; gap: 8px !important; }
          .dp-stat-num { font-size: 22px !important; }
        }
      `}</style>

      <PracticeQueueRail
        width={railWidth}
        items={queue}
        currentIndex={currentIndex}
        elapsedSeconds={elapsed}
        plannedSeconds={PLANNED_SESSION_SECONDS}
      />

      <div className="flex-1 flex flex-col min-w-0" role="region" aria-label="Stage centre">
        <div className="h-16 px-6 flex items-center justify-between border-b border-[var(--rule)]">
          <div
            role="status"
            aria-label={`Session active · piece ${currentIndex + 1} of ${total}`}
            className="flex items-center gap-3"
          >
            <span
              aria-hidden="true"
              className={cn(
                'w-2 h-2 rounded-full bg-[var(--warm)]',
                isRunning && 'animate-pulse',
              )}
            />
            <span className="text-[11px] font-mono tracking-[0.18em] text-[var(--ink-3)] uppercase">
              Session active · piece {currentIndex + 1} of {total}
            </span>
          </div>
          <button
            type="button"
            aria-label="End Practice"
            onClick={onEndPractice}
            disabled={isAssessing}
            className="text-[12px] font-semibold text-[var(--ink-3)] hover:text-[var(--ink)]"
          >
            End session
          </button>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-8 min-h-0">
          {currentItem && (
            <>
              <span className="text-[10px] font-mono tracking-[0.18em] text-[var(--ink-3)] uppercase">
                {[currentItem.formName, currentItem.structurePattern, currentItem.composerName].filter(Boolean).join(' · ') || currentItem.arrangementName}
              </span>
              <h1 className="dp-title font-display text-[56px] font-semibold tracking-tight mt-3 leading-[1] text-center">
                {currentItem.pieceName}
              </h1>
            </>
          )}

          <div className="dp-stats mt-8 grid grid-cols-3 gap-3 w-full max-w-[520px] min-w-0">
            <div
              role="status"
              aria-label="Tempo"
              className="min-w-0 text-center p-3 rounded-[var(--r-md)] bg-[var(--bg-soft)] border border-[var(--rule)]"
            >
              <div className="text-[9px] font-mono tracking-[0.16em] text-[var(--ink-3)] uppercase truncate">Tempo</div>
              <div
                className={cn(
                  'dp-stat-num font-display text-[26px] font-semibold mt-1 tabular-nums',
                  metronomeOn && 'pulse-bpm',
                )}
              >{bpm}</div>
              <div className="text-[9px] text-[var(--ink-3)] font-mono truncate">BPM</div>
            </div>
            <div
              role="status"
              aria-label="Elapsed"
              className="min-w-0 text-center p-3 rounded-[var(--r-md)] bg-[var(--brand-wash)] border border-[var(--rule)]"
            >
              <div className="text-[9px] font-mono tracking-[0.16em] text-[var(--ink-3)] uppercase truncate">Elapsed</div>
              <div className="dp-stat-num font-display text-[26px] font-semibold mt-1 tabular-nums">{formatMinutesSeconds(elapsed)}</div>
            </div>
            <div
              role="status"
              aria-label="Loop"
              className="min-w-0 text-center p-3 rounded-[var(--r-md)] bg-[var(--bg-soft)] border border-[var(--rule)]"
            >
              <div className="text-[9px] font-mono tracking-[0.16em] text-[var(--ink-3)] uppercase truncate">Loop</div>
              <div className="dp-stat-num font-display text-[26px] font-semibold mt-1">{currentItem?.structurePattern ?? '—'}</div>
              <div className="text-[9px] text-[var(--ink-3)] font-mono truncate">Pattern</div>
            </div>
          </div>

          <div className="mt-10 flex items-center gap-3">
            <button
              type="button"
              aria-label={isRunning ? 'Pause practice' : 'Resume practice'}
              onClick={handleToggleTimer}
              className="w-20 h-20 rounded-full bg-[var(--bg-soft)] text-[var(--ink)] hover:bg-[var(--rule)] font-semibold"
            >
              {isRunning ? '❚❚' : '▶'}
            </button>
            <button
              type="button"
              aria-label="Skip"
              onClick={onSkip}
              disabled={isAssessing}
              className="w-14 h-14 rounded-full text-[var(--ink-3)] hover:bg-[var(--bg-soft)] disabled:opacity-50"
            >
              ›
            </button>
          </div>

          {currentItem && (
            <div className="mt-4 flex justify-center">
              <RecordTakeFlow arrangementId={currentItem.arrangementId} />
            </div>
          )}

          <div className="mt-8 w-full max-w-[520px]">
            <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-[var(--ink-3)] text-center">
              How did it go?
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {sortedLevels.map((level) => (
                <button
                  key={level.assessmentLevelId}
                  type="button"
                  onClick={() => onAssess(level.assessmentLevelId)}
                  disabled={isAssessing}
                  className={cn(
                    'flex min-h-[48px] items-center justify-center rounded-[var(--r-md)] border border-[var(--rule)]',
                    'bg-[var(--bg-soft)] px-3 py-3 text-sm font-bold text-[var(--ink)]',
                    'transition-all hover:border-[var(--brand)]',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                  aria-label={level.prettyName}
                >
                  {level.prettyName}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={onFinish}
              className="mt-4 w-full text-[12px] font-semibold text-[var(--ink-3)] hover:text-[var(--ink)]"
            >
              Finish session
            </button>
          </div>
        </div>

        {variant === 'tablet' && (
          // Tablet keeps the slim metronome strip at the foot of the centre
          // column; the drone now lives in the shared side panel below (#405).
          <PracticeControlsStrip
            metronomeOn={metronomeOn}
            onMetronomeOnChange={handleMetronomeOnChange}
            bpm={bpm}
          />
        )}
      </div>

      {variant === 'desktop' && (
        <PracticeControlsRail
          notes={currentItem?.notes ?? null}
          resources={resources}
          metronomeOn={metronomeOn}
          onMetronomeOnChange={handleMetronomeOnChange}
          bpm={bpm}
          onBpmChange={setBpm}
          activeMultiplier={activeMultiplier}
          onActiveMultiplierChange={setActiveMultiplier}
          droneRoot={drone.root}
          onDroneRootChange={drone.setRoot}
          droneInterval={drone.interval}
          onDroneIntervalChange={drone.setInterval}
          droneOctave={drone.octave}
          onDroneOctaveChange={drone.setOctave}
          droneVolume={drone.volume}
          onDroneVolumeChange={drone.setVolume}
          droneOn={drone.on}
          onDroneOnChange={drone.setOn}
          onDroneAutoMatch={handleDroneAutoMatch}
          droneAutoMatchDisabled={tuneKey === null}
        />
      )}

      {variant === 'tablet' && (
        // Tablet landscape adopts the shared 12-key control in a right side
        // panel, matching the design's TPracticeLandscapeWithDrone frame (#405).
        <aside
          data-practice-drone-panel
          className="w-[300px] shrink-0 border-l border-[var(--rule)] flex flex-col bg-[var(--bg-surface)] text-[var(--ink)] min-h-0"
        >
          <div className="h-16 px-5 flex items-center border-b border-[var(--rule)] shrink-0">
            <div className="text-[13px] font-semibold">Drone</div>
          </div>
          <div className="flex-1 overflow-y-auto nice-scroll min-h-0">
            <DroneControl
              surface="light"
              compact
              root={drone.root}
              interval={drone.interval}
              octave={drone.octave}
              volume={drone.volume}
              on={drone.on}
              onRootChange={drone.setRoot}
              onIntervalChange={drone.setInterval}
              onOctaveChange={drone.setOctave}
              onVolumeChange={drone.setVolume}
              onOnChange={drone.setOn}
              onAutoMatch={handleDroneAutoMatch}
              autoMatchDisabled={tuneKey === null}
            />
          </div>
        </aside>
      )}
    </div>
  );
}

type TabletPortraitStageProps = Omit<PracticeStageProps, 'variant'>;

function droneTriggerValue(root: DroneKey | null, on: boolean): string {
  return on && root !== null ? `${root} · playing` : 'Off';
}

// Tablet portrait has no room for the landscape side panel (#451). The drone
// becomes a labelled trigger in the foot controls strip that opens the shared
// PracticeBottomSheet holding <DroneControl surface="light" compact/>. The
// collapsed trigger reads the lifted playing state ("D · playing" / "Off").
function TabletPortraitStage({
  startTime,
  isRunning,
  queue,
  currentIndex,
  assessmentLevels,
  onToggleTimer,
  onFinish,
  onAssess,
  onSkip,
  onEndPractice,
  isAssessing,
}: TabletPortraitStageProps): ReactElement {
  const [, tick] = useReducer((n: number) => n + 1, 0);
  const currentItem = queue[currentIndex];
  const [bpm, setBpm] = useState<number>(currentItem?.practiceTempoBeats ?? DEFAULT_BPM);
  const [metronomeOn, setMetronomeOn] = useState<boolean>(false);
  const [droneOpen, setDroneOpen] = useState<boolean>(false);
  const drone = useDroneState();
  const tuneKey = parseTuneKey(currentItem?.keyName ?? null);

  function handleDroneAutoMatch(): void {
    if (tuneKey === null) return;
    drone.setRoot(tuneKey);
    drone.setOn(true);
  }

  useEffect(() => {
    setBpm(currentItem?.practiceTempoBeats ?? DEFAULT_BPM);
  }, [currentItem?.userArrangementId, currentItem?.practiceTempoBeats]);

  function handleMetronomeOnChange(next: boolean): void {
    if (next) void resumeAudioContext();
    setMetronomeOn(next);
  }

  function handleToggleTimer(): void {
    void resumeAudioContext();
    onToggleTimer();
  }

  useMetronome({ bpm, multiplier: 1, on: metronomeOn });

  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [isRunning]);

  const total = queue.length;
  const elapsed = elapsedSecondsSince(startTime);
  const sortedLevels = [...assessmentLevels].sort((a, b) => a.sortOrder - b.sortOrder);
  const dronePlaying = drone.on && drone.root !== null;

  return (
    <div className="relative flex h-[calc(100vh-4rem)] flex-col bg-[var(--bg-page)] text-[var(--ink)]">
      <div className="flex h-16 shrink-0 items-center justify-between border-b border-[var(--rule)] bg-[var(--bg-surface)] px-6">
        <div
          role="status"
          aria-label={`Session active · piece ${currentIndex + 1} of ${total}`}
          className="flex items-center gap-3"
        >
          <span
            aria-hidden="true"
            className={cn('h-2 w-2 rounded-full bg-[var(--warm)]', isRunning && 'animate-pulse')}
          />
          <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-3)]">
            Session active · piece {currentIndex + 1} of {total}
          </span>
        </div>
        <button
          type="button"
          aria-label="End Practice"
          onClick={onEndPractice}
          disabled={isAssessing}
          className="text-[12px] font-semibold text-[var(--ink-3)] hover:text-[var(--ink)]"
        >
          End session
        </button>
      </div>

      <div
        className="flex min-h-0 flex-1 flex-col items-center justify-center px-8 text-center"
        role="region"
        aria-label="Stage centre"
      >
        {currentItem && (
          <>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-3)]">
              {[currentItem.formName, currentItem.structurePattern, currentItem.composerName]
                .filter(Boolean)
                .join(' · ') || currentItem.arrangementName}
            </span>
            <h1 className="mt-3 font-display text-[44px] font-semibold leading-[1] tracking-tight">
              {currentItem.pieceName}
            </h1>
          </>
        )}

        <div
          role="status"
          aria-label="Elapsed"
          className="mt-8 font-display text-[40px] font-semibold tabular-nums"
        >
          {formatMinutesSeconds(elapsed)}
        </div>

        <div className="mt-8 flex items-center gap-3">
          <button
            type="button"
            aria-label={isRunning ? 'Pause practice' : 'Resume practice'}
            onClick={handleToggleTimer}
            className="h-20 w-20 rounded-full bg-[var(--bg-soft)] font-semibold text-[var(--ink)] hover:bg-[var(--rule)]"
          >
            {isRunning ? '❚❚' : '▶'}
          </button>
          <button
            type="button"
            aria-label="Skip"
            onClick={onSkip}
            disabled={isAssessing}
            className="h-14 w-14 rounded-full text-[var(--ink-3)] hover:bg-[var(--bg-soft)] disabled:opacity-50"
          >
            ›
          </button>
        </div>

        {currentItem && (
          <div className="mt-4 flex justify-center">
            <RecordTakeFlow arrangementId={currentItem.arrangementId} />
          </div>
        )}

        <div className="mt-8 w-full max-w-[520px]">
          <p className="mb-3 text-center text-[10px] font-bold uppercase tracking-widest text-[var(--ink-3)]">
            How did it go?
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {sortedLevels.map((level) => (
              <button
                key={level.assessmentLevelId}
                type="button"
                onClick={() => onAssess(level.assessmentLevelId)}
                disabled={isAssessing}
                className={cn(
                  'flex min-h-[48px] items-center justify-center rounded-[var(--r-md)] border border-[var(--rule)]',
                  'bg-[var(--bg-soft)] px-3 py-3 text-sm font-bold text-[var(--ink)]',
                  'transition-all hover:border-[var(--brand)]',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
                aria-label={level.prettyName}
              >
                {level.prettyName}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onFinish}
            className="mt-4 w-full text-[12px] font-semibold text-[var(--ink-3)] hover:text-[var(--ink)]"
          >
            Finish session
          </button>
        </div>
      </div>

      <div className="shrink-0 border-t border-[var(--rule)] bg-[var(--bg-surface)] px-6 pb-6 pt-3">
        <div className="mb-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-3)]">
          Practice controls
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <div className="flex items-center gap-3 rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)] p-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--ink-3)]">
              Metronome
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={metronomeOn}
              aria-label="Metronome"
              onClick={() => handleMetronomeOnChange(!metronomeOn)}
              className={cn(
                'h-6 w-10 rounded-full p-0.5 transition-colors',
                metronomeOn ? 'bg-[var(--warm)]' : 'bg-[var(--bg-soft)]',
              )}
            >
              <span
                className={cn(
                  'block h-5 w-5 rounded-full bg-[var(--bg-surface)] transition-transform',
                  metronomeOn ? 'translate-x-4' : 'translate-x-0',
                )}
              />
            </button>
            <span className="ml-auto font-display text-[18px] font-semibold tabular-nums">{bpm}</span>
          </div>
          <ControlTile
            icon="wave"
            label="Drone"
            value={droneTriggerValue(drone.root, drone.on)}
            active={dronePlaying}
            expanded={droneOpen}
            accent="brand"
            onClick={() => setDroneOpen(true)}
          />
        </div>
      </div>

      <PracticeBottomSheet
        open={droneOpen}
        eyebrow="Practice control"
        title="Drone"
        onClose={() => setDroneOpen(false)}
        status={
          dronePlaying ? (
            <span className="inline-flex h-6 items-center gap-1.5 rounded-full bg-[var(--bg-soft)] px-2">
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--warm)]"
              />
              <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-[var(--ink-2)]">
                Playing
              </span>
            </span>
          ) : undefined
        }
      >
        <DroneControl
          surface="light"
          compact
          showHeader
          root={drone.root}
          interval={drone.interval}
          octave={drone.octave}
          volume={drone.volume}
          on={drone.on}
          onRootChange={drone.setRoot}
          onIntervalChange={drone.setInterval}
          onOctaveChange={drone.setOctave}
          onVolumeChange={drone.setVolume}
          onOnChange={drone.setOn}
          onAutoMatch={handleDroneAutoMatch}
          autoMatchDisabled={tuneKey === null}
        />
      </PracticeBottomSheet>
    </div>
  );
}
